import {reactive, onMounted} from 'vue';
import {useVuelidate} from '@vuelidate/core';
import {required, email, helpers} from '@vuelidate/validators';
import {useAuthStore} from '@stores/auth';
import {useAlert} from '@stores/alert';
import ApiService from '@services/ApiService';
import type {AccountState} from '@interfaces/User';

/**
 * A composable to manage user account data.
 * @returns {object} Reactive state and methods for user account management.
 */
export function useAccount() {
    const {showAlert} = useAlert();
    const authStore = useAuthStore();
    const loading = reactive({value: false});

    const state = reactive<AccountState>({
        account: {
            id: '',
            name: '',
            document: '',
            email: '',
            phone: '',
            nationality: '',
            gender: 0,
            birthDate: '',
            created_at: '',
            status: '',
            is_email_valid: false,
            is_phone_valid: false,
        },
        address: {
            state: '',
            zipCode: '',
            number: '',
            city: '',
            street: '',
            neighborhood: '',
        },
    });

    const formerrors = reactive({
        account: {},
        address: {},
    });

    const rules = {
        account: {
            id: {required: helpers.withMessage('Campo obrigatorio', required)},
            name: {required: helpers.withMessage('Campo obrigatorio', required)},
            document: {required: helpers.withMessage('Campo obrigatorio', required)},
            email: {
                required: helpers.withMessage('Campo obrigatorio', required),
                email: helpers.withMessage('Informe um E-mail valido', email)
            },
            phone: {required: helpers.withMessage('Campo obrigatorio', required)},
            nationality: {required: helpers.withMessage('Campo obrigatorio', required)},
            gender: {required: helpers.withMessage('Campo obrigatorio', required)},
            birthDate: {required: helpers.withMessage('Campo obrigatorio', required)},
        },
        address: {
            state: {required: helpers.withMessage('Campo obrigatorio', required)},
            zipCode: {required: helpers.withMessage('Campo obrigatorio', required)},
            neighborhood: {required: helpers.withMessage('Campo obrigatorio', required)},
            number: {required: helpers.withMessage('Campo obrigatorio', required)},
            city: {required: helpers.withMessage('Campo obrigatorio', required)},
            street: {required: helpers.withMessage('Campo obrigatorio', required)},
        },
    };

    const v$ = useVuelidate(rules, state);

    onBeforeMount(async () => {
        const userData = authStore.user;

        if (userData) {
            state.account.id = userData.id;
            state.account.name = userData.name;
            state.account.email = userData.email;
            state.account.birthDate = userData?.birthday;
            state.account.phone = userData.phone;

            state.account.is_email_valid = userData.is_email_valid;
            state.account.is_phone_valid = userData.is_phone_valid;

            /**
             * TODO: searching nationality on backend user docs
             */
            state.account.nationality = 'Brasileiro';
            state.account.gender = userData.gender;
            state.account.document = userData.document.number;
            state.address.street = userData.address?.street;
            state.address.zipCode = userData.address?.zip_code;
            state.address.city = userData.address?.city;
            state.address.state = userData.address?.state;
            state.address.number = userData.address?.number;
            state.address.neighborhood = userData.address?.neighborhood;
            state.account.created_at = userData.created_at;
            state.account.status = userData.status;
        }
    });

    async function zipcode() {
        if (state.address.zipCode.length) {
            const cep = state.address.zipCode.replace('-', '');

            try {
                const res = await $fetch<{
                    logradouro: string;
                    localidade: string;
                    uf: string;
                    bairro: string;
                }>(`https://viacep.com.br/ws/${cep}/json`);

                if ('erro' in res) {
                    formerrors.address = {
                        zipCode: 'CEP não encontrado'
                    }

                    return;
                }

                state.address.state = res.uf;
                state.address.city = res.localidade;
                state.address.neighborhood = res?.bairro;
                state.address.street = res?.logradouro;
            } catch (error) {
                formerrors.address = {
                    zipCode: 'Cep Inválido'
                }
            }
        }
    }

    async function submitAccount() {
        v$.value.account.$validate();
        v$.value.address.$validate();

        if (!v$.value.address.$error && !v$.value.account.$error) {
            try {
                loading.value = true;
                let response = await ApiService.post('/api/userUpdate', {
                    ...state.account,
                    ...state.address,
                });

                response = await response.data;
                loading.value = false;
                if (response.success) {
                    showAlert('success', response.message);
                } else {
                    showAlert('error', response.message);
                }
            } catch (error) {
                console.error(error);
            }
        }
    }

    return {
        state,
        formerrors,
        v$,
        loading,
        zipcode,
        submitAccount,
    };
}
